import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FaSearch, FaSlidersH, FaHistory, FaTags, FaPrint,
} from 'react-icons/fa';
import Pagination, { PaginationMeta, EMPTY_PAGINATION } from './Pagination';
import { generarEtiquetasPDF, EtiquetaArticulo } from '../../services/etiquetaService';
import { useAuth } from '../../context/AuthContext';
import AjusteStockModal from './AjusteStockModal';
import HistorialStockModal from './HistorialStockModal';
import styles from './styles/Articulos.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/** Sucursal: cada una es una columna de stock en la tabla. */
interface Local {
    locales_id: number;
    nombre_local: string;
}

/** Fila del listado: un articulo con su stock abierto por sucursal. */
interface Articulo {
    idProductos: number;
    sku: string;
    producto: string;
    variante: string;
    categoria: string;
    precio: number;
    /** Stock por local, indexado por locales_id en string. */
    stock_por_local: Record<string, number>;
    stock_total: number;
    stock_minimo: number;
    estado: 'Normal' | 'Bajo' | 'Critico';
}

type EstadoFiltro = 'Todos' | 'Normal' | 'Bajo' | 'Critico';

const ESTADOS: EstadoFiltro[] = ['Todos', 'Normal', 'Bajo', 'Critico'];

/** Nombre corto de la sucursal para el encabezado ("Sucursal Centro" -> "S. Centro"). */
const nombreCorto = (nombre: string) => nombre.replace(/^Sucursal\s+/i, 'S. ');

const Articulos: React.FC = () => {
    const { user } = useAuth();

    // Acciones rapidas: ambas abren un modal sobre esta misma pantalla.
    const [ajusteOpen, setAjusteOpen] = useState(false);
    const [historialOpen, setHistorialOpen] = useState(false);

    const [locales, setLocales] = useState<Local[]>([]);
    const [articulos, setArticulos] = useState<Articulo[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [categorias, setCategorias] = useState<string[]>([]);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filtros de la barra superior.
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [local, setLocal] = useState('Todas');
    const [categoria, setCategoria] = useState('Todas');
    const [estado, setEstado] = useState<EstadoFiltro>('Todos');

    // El buscador no dispara un fetch por tecla.
    useEffect(() => {
        const id = window.setTimeout(() => setDebouncedSearch(search), 300);
        return () => window.clearTimeout(id);
    }, [search]);

    useEffect(() => {
        fetch(`${API}/categorias`)
            .then(res => res.json())
            .then(data => {
                if (!Array.isArray(data)) return;
                setCategorias(data.map((c: any) => c.nombre).filter(Boolean));
            })
            .catch(() => { /* el selector queda con "Todas": la tabla igual funciona */ });
    }, []);

    const fetchArticulos = useCallback(async () => {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
        if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
        if (categoria !== 'Todas') params.set('categoria', categoria);
        if (estado !== 'Todos') params.set('estado', estado);
        if (local !== 'Todas') params.set('id_local', local);

        try {
            const res = await fetch(`${API}/admin/stock/articulos?${params.toString()}`);
            if (!res.ok) throw new Error('respuesta no ok');
            const data = await res.json();
            setArticulos(Array.isArray(data.articulos) ? data.articulos : []);
            setLocales(Array.isArray(data.locales) ? data.locales : []);
            setMeta(data.pagination ?? EMPTY_PAGINATION);
        } catch (err) {
            console.error('Error cargando articulos', err);
            setError('No se pudieron cargar los artículos.');
            setArticulos([]);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, debouncedSearch, categoria, estado, local]);

    useEffect(() => { fetchArticulos(); }, [fetchArticulos]);

    // Cambiar un filtro vuelve a la primera pagina: quedarse en la 4 con un
    // filtro nuevo mostraria una tabla vacia sin motivo aparente.
    useEffect(() => { setPage(1); }, [debouncedSearch, categoria, estado, local, perPage]);

    const totalUnidades = useMemo(
        () => articulos.reduce((acc, a) => acc + (a.stock_total || 0), 0),
        [articulos],
    );

    /* ------------------------------------------------------------- etiquetas */

    // Articulos tildados para imprimir etiqueta de gondola.
    const [seleccion, setSeleccion] = useState<Set<number>>(new Set());

    const toggleEtiqueta = (id: number) => {
        setSeleccion(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Solo se tildan/destildan los de la pagina visible: el "todos" de una
    // tabla paginada nunca deberia alcanzar filas que no estan a la vista.
    const visiblesSeleccionados = articulos.length > 0
        && articulos.every(a => seleccion.has(a.idProductos));

    const toggleTodos = () => {
        setSeleccion(prev => {
            const next = new Set(prev);
            if (visiblesSeleccionados) articulos.forEach(a => next.delete(a.idProductos));
            else articulos.forEach(a => next.add(a.idProductos));
            return next;
        });
    };

    const imprimirEtiquetas = (items: Articulo[]) => {
        const etiquetas: EtiquetaArticulo[] = items.map(a => ({
            codigo: a.sku,
            nombre: a.producto,
        }));
        if (etiquetas.length > 0) generarEtiquetasPDF(etiquetas);
    };

    const imprimirSeleccion = () => {
        // Se respeta el orden de la tabla, no el de tildado.
        imprimirEtiquetas(articulos.filter(a => seleccion.has(a.idProductos)));
    };

    return (
        <div className={styles.art}>
            <header className={styles.head}>
                <h2 className={styles.headTitle}>Artículos</h2>
                <p className={styles.headSub}>Productos y existencias por sucursal</p>
            </header>

            <div className={styles.layout}>
                <div className={styles.main}>
                    <div className={styles.filters}>
                        <div className={styles.searchBox}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="search"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar por SKU, Producto, Estado…"
                                aria-label="Buscar artículos"
                            />
                        </div>

                        <label className={styles.filterField}>
                            <span>Sucursal:</span>
                            <select value={local} onChange={e => setLocal(e.target.value)}>
                                <option value="Todas">Todas las Sucursales</option>
                                {locales.map(l => (
                                    <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                                ))}
                            </select>
                        </label>

                        <label className={styles.filterField}>
                            <span>Categoría:</span>
                            <select value={categoria} onChange={e => setCategoria(e.target.value)}>
                                <option value="Todas">Todas las Categorías</option>
                                {categorias.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>

                        <label className={styles.filterField}>
                            <span>Estado:</span>
                            <select
                                value={estado}
                                onChange={e => setEstado(e.target.value as EstadoFiltro)}
                            >
                                {ESTADOS.map(s => (
                                    <option key={s} value={s}>
                                        {s === 'Todos' ? 'Todos los Estados' : s}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {error && <div className={styles.errorBar}>{error}</div>}

                    {seleccion.size > 0 && (
                        <div className={styles.selectionBar}>
                            <span>
                                {seleccion.size} artículo{seleccion.size === 1 ? '' : 's'} seleccionado
                                {seleccion.size === 1 ? '' : 's'} para etiquetas
                            </span>
                            <div className={styles.selectionActions}>
                                <button
                                    type="button"
                                    className={styles.ghostBtn}
                                    onClick={() => setSeleccion(new Set())}
                                >
                                    Limpiar
                                </button>
                                <button
                                    type="button"
                                    className={styles.primaryBtn}
                                    onClick={imprimirSeleccion}
                                >
                                    <FaPrint /> Imprimir etiquetas
                                </button>
                            </div>
                        </div>
                    )}

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>SKU</th>
                                    <th>Producto</th>
                                    <th>Variante</th>
                                    {locales.map(l => (
                                        <th key={l.locales_id} className={styles.num}>
                                            {nombreCorto(l.nombre_local)}
                                        </th>
                                    ))}
                                    <th className={styles.num}>Stock Total</th>
                                    <th className={styles.num}>Mínimo</th>
                                    <th>Estado</th>
                                    <th className={styles.colEtiqueta}>
                                        <label className={styles.checkAll}>
                                            <input
                                                type="checkbox"
                                                checked={visiblesSeleccionados}
                                                onChange={toggleTodos}
                                                aria-label="Seleccionar todos para etiquetas"
                                            />
                                            Etiqueta
                                        </label>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={locales.length + 7} className={styles.empty}>
                                            Cargando artículos…
                                        </td>
                                    </tr>
                                )}

                                {!loading && articulos.length === 0 && (
                                    <tr>
                                        <td colSpan={locales.length + 7} className={styles.empty}>
                                            No hay artículos para los filtros seleccionados.
                                        </td>
                                    </tr>
                                )}

                                {!loading && articulos.map(a => (
                                    <tr key={a.idProductos}>
                                        <td className={styles.cellSku}>{a.sku}</td>
                                        <td className={styles.cellName} title={a.producto}>{a.producto}</td>
                                        <td className={styles.cellVariant}>{a.variante || '—'}</td>
                                        {locales.map(l => (
                                            <td key={l.locales_id} className={styles.numMono}>
                                                {a.stock_por_local[String(l.locales_id)] ?? 0}
                                            </td>
                                        ))}
                                        <td className={`${styles.num} ${styles.strong}`}>{a.stock_total}</td>
                                        <td className={styles.numMuted}>{a.stock_minimo}</td>
                                        <td>
                                            <span className={`${styles.badge} ${styles[`badge${a.estado}`]}`}>
                                                {a.estado === 'Critico' ? 'Crítico' : a.estado}
                                            </span>
                                        </td>
                                        <td className={styles.colEtiqueta}>
                                            <div className={styles.etiquetaCell}>
                                                <input
                                                    type="checkbox"
                                                    checked={seleccion.has(a.idProductos)}
                                                    onChange={() => toggleEtiqueta(a.idProductos)}
                                                    aria-label={`Seleccionar ${a.producto} para etiqueta`}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles.iconBtn}
                                                    title="Imprimir etiqueta de este artículo"
                                                    aria-label={`Imprimir etiqueta de ${a.producto}`}
                                                    onClick={() => imprimirEtiquetas([a])}
                                                >
                                                    <FaTags />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        meta={meta}
                        onPageChange={setPage}
                        onPerPageChange={setPerPage}
                        itemLabel="artículos"
                    />
                </div>

                <aside className={styles.side}>
                    <section className={styles.panel}>
                        <h3 className={styles.panelTitle}>Acciones Rápidas</h3>

                        <button
                            type="button"
                            className={styles.action}
                            onClick={() => setAjusteOpen(true)}
                        >
                            <FaSlidersH className={styles.actionIcon} />
                            <span>
                                <strong>Ajuste de Stock</strong>
                                <small>Registrar una entrada o una salida</small>
                            </span>
                        </button>

                        <button
                            type="button"
                            className={styles.action}
                            onClick={() => setHistorialOpen(true)}
                        >
                            <FaHistory className={styles.actionIcon} />
                            <span>
                                <strong>Historial de Stock</strong>
                                <small>Movimientos, ventas y ajustes</small>
                            </span>
                        </button>
                    </section>

                    <section className={styles.note}>
                        <strong>Sincronización Multi-Sucursal</strong>
                        <p>
                            El stock se actualiza en tiempo real con las ventas del POS y
                            la tienda online.
                        </p>
                    </section>

                    <section className={styles.resumen}>
                        <span className={styles.resumenLabel}>Unidades en pantalla</span>
                        <span className={styles.resumenValue}>
                            {totalUnidades.toLocaleString('es-AR')}
                        </span>
                    </section>
                </aside>
            </div>

            {ajusteOpen && (
                <AjusteStockModal
                    locales={locales}
                    usuario={user || 'Admin'}
                    onClose={() => setAjusteOpen(false)}
                    // El ajuste cambia las existencias que muestra la tabla:
                    // se recarga para no dejar en pantalla un stock viejo.
                    onSaved={() => { fetchArticulos(); }}
                />
            )}

            {historialOpen && (
                <HistorialStockModal
                    locales={locales}
                    onClose={() => setHistorialOpen(false)}
                />
            )}
        </div>
    );
};

export default Articulos;
